/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ClipboardPaste, ImagePlus, Loader2, Search, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { usePasteImage } from "@/hooks/usePasteImage";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// "+ New image" for one carousel slide: upload a file, paste from the
// clipboard, or pick an Unsplash / Pexels photo. Every route stores the
// photo in our own storage (routes/carousel.py) and adds it to the
// article's carousel library, so it shows in every slide's Photo menu.
// With purpose="panorama" it adds the carousel's panorama instead:
// upload / paste only (stock photos aren't wide enough), stored wide.
// With purpose="facebook" (FacebookPhotoPicker) it adds a photo to a
// Facebook Photo post; stored exactly like a slide photo, only the
// wording differs.

const TABS = [
  { id: "upload", label: "Upload" },
  { id: "paste", label: "Paste" },
  { id: "unsplash", label: "Unsplash" },
  { id: "pexels", label: "Pexels" },
];
const PANORAMA_TABS = ["upload", "paste"];
const INPUT = "w-full px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]";
const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

function StockTab({ article, provider, onPick, busyUrl, disabled }) {
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const photos = useQuery({
    queryKey: ["stock-photos", article.id, provider, query],
    queryFn: () => api.getStockPhotos(article.id, { q: query || undefined, provider }),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const groups = photos.data?.groups || [];
  const tiles = groups.flatMap((g) => (g.photos || []).map((p) => ({ ...p, group: g.concept })));
  const groupError = groups.map((g) => g.error).find(Boolean);
  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(draft.trim());
        }}
      >
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
          <input
            data-testid={`carousel-image-search-${provider}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search… (blank = this article's photo ideas)"
            className="w-full text-sm pl-8 pr-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={photos.isFetching}
          className="text-xs px-3 py-1.5 border border-[var(--ink)] bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-40"
        >
          Search
        </button>
      </form>
      {photos.isLoading && (
        <div className="flex items-center gap-2 text-xs text-[var(--ink-muted)]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading photos…
        </div>
      )}
      {photos.isError && (
        <div className="text-xs text-[var(--ink-muted)] italic">
          {errText(photos.error, "Could not load photos")}
        </div>
      )}
      {photos.isSuccess && tiles.length === 0 && (
        <div className="text-xs text-[var(--ink-muted)] italic">
          {groupError || "No photos — try a search above."}
        </div>
      )}
      {tiles.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[46vh] overflow-y-auto">
          {tiles.map((p) => (
            <button
              key={`${p.provider}-${p.id}-${p.group}`}
              type="button"
              data-testid={`carousel-image-stock-${p.id}`}
              disabled={disabled}
              onClick={() => onPick(p)}
              title={p.alt || p.credit}
              className="relative aspect-[4/5] bg-[var(--secondary)] overflow-hidden border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
            >
              <img src={p.thumb_url} alt={p.alt || p.group} loading="lazy" className="w-full h-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 px-1.5 py-0.5 text-[10px] text-white bg-black/45 truncate text-left">
                {p.photographer || p.credit}
              </span>
              {busyUrl === p.download_url && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CarouselImageDialog({ open, onOpenChange, article, onAdded, purpose = "slide" }) {
  const isPano = purpose === "panorama";
  const isFb = purpose === "facebook";
  const [tab, setTab] = useState("upload");
  const [credit, setCredit] = useState("");
  const [preview, setPreview] = useState(null); // {file, url, source}
  const fileRef = useRef(null);

  // Reset between openings.
  useEffect(() => {
    if (!open) {
      setPreview(null);
      setCredit("");
    }
  }, [open]);
  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);
  useEffect(() => {
    if (open && isPano && !PANORAMA_TABS.includes(tab)) setTab("upload");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPano]);

  const done = (r) => {
    toast.success(
      isPano ? "Panorama added across every slide" : isFb ? "Photo added to the post" : "Photo added to this slide",
    );
    onAdded(r.image, r.carousel_images);
    onOpenChange(false);
  };

  const upload = useMutation({
    mutationFn: ({ file, source }) => api.uploadCarouselImage(article.id, file, { source, credit, purpose }),
    onSuccess: done,
    onError: (e) => toast.error(errText(e, "Upload failed")),
  });
  const importStock = useMutation({
    mutationFn: (p) =>
      api.importCarouselImage(article.id, {
        kind: p.provider,
        url: p.download_url,
        credit: p.credit,
        download_location: p.download_location,
      }),
    onSuccess: done,
    onError: (e) => toast.error(errText(e, "Could not add that photo")),
  });
  const busy = upload.isPending || importStock.isPending;

  const choose = (file, source) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("That file is not an image.");
      return;
    }
    setPreview({ file, source, url: URL.createObjectURL(file) });
  };

  // Ctrl/Cmd+V while the dialog is open. Goes through the app's paste
  // stack (lib/clipboardImage.js), so the dialog takes the paste instead
  // of the article page, which would otherwise set the featured image.
  const { pasteFromClipboard, canReadClipboard } = usePasteImage({
    enabled: open,
    onImage: (file) => {
      setTab("paste");
      choose(file, "paste");
    },
    onError: (msg) => toast.error(msg),
  });
  const readClipboard = () => {
    if (!canReadClipboard) {
      toast.message("This browser can't read the clipboard directly — press Ctrl+V (Cmd+V on a Mac).");
      return;
    }
    pasteFromClipboard();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="carousel-image-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{isPano ? "Panorama photo" : "New image"}</DialogTitle>
          <DialogDescription className="text-sm text-[var(--ink-muted)]">
            {isPano
              ? "A wide photo (e.g. a city skyline) spread across every slide as one continuous picture. Kept at full width."
              : isFb
              ? "The photo is added to this Facebook post and saved with the article's photos."
              : "The photo goes on this slide and joins the Photo menu for every slide."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 text-xs" role="tablist">
          {TABS.filter((t) => !isPano || PANORAMA_TABS.includes(t.id)).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`carousel-image-tab-${t.id}`}
              disabled={busy}
              onClick={() => {
                setTab(t.id);
                setPreview(null);
              }}
              className={
                "px-3 py-1 border " +
                (tab === t.id
                  ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                  : "border-[var(--border)] hover:border-[var(--ink)]")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {(tab === "upload" || tab === "paste") && (
          <div className="space-y-3">
            {preview ? (
              <div className="flex gap-3 items-start">
                <img src={preview.url} alt="" className="w-32 h-40 object-cover border border-[var(--border)]" />
                <div className="text-xs text-[var(--ink-muted)] space-y-2">
                  <div className="truncate max-w-[260px]">{preview.file.name}</div>
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setPreview(null)}
                    disabled={busy}
                  >
                    Choose a different image
                  </button>
                </div>
              </div>
            ) : tab === "upload" ? (
              <div
                className="border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--ink-muted)] cursor-pointer hover:border-[var(--ink)]"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  choose(e.dataTransfer.files?.[0], "upload");
                }}
                data-testid="carousel-image-dropzone"
              >
                <Upload className="w-5 h-5 mx-auto mb-2" />
                Drop an image here, or click to choose a file.
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    choose(e.target.files?.[0], "upload");
                    e.target.value = "";
                  }}
                />
              </div>
            ) : (
              <div
                className="border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--ink-muted)] space-y-3"
                data-testid="carousel-image-pastezone"
              >
                <ClipboardPaste className="w-5 h-5 mx-auto" />
                <div>Copy an image, then press Ctrl+V (Cmd+V on a Mac).</div>
                <button
                  type="button"
                  onClick={readClipboard}
                  className="text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--ink)]"
                >
                  Paste from clipboard
                </button>
              </div>
            )}
            <div>
              <label className="overline mb-1.5 block" htmlFor="carousel-image-credit">Credit line</label>
              <input
                id="carousel-image-credit"
                className={INPUT}
                value={credit}
                maxLength={120}
                onChange={(e) => setCredit(e.target.value)}
                placeholder="e.g. Photo: Oliver Quintero / Gringo Gazette"
              />
            </div>
            <button
              type="button"
              data-testid="carousel-image-add"
              disabled={!preview || busy}
              onClick={() => upload.mutate({ file: preview.file, source: preview.source })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
            >
              {upload.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              {isPano ? "Use as panorama" : isFb ? "Add to post" : "Add to slide"}
            </button>
          </div>
        )}

        {!isPano && (tab === "unsplash" || tab === "pexels") && (
          <StockTab
            key={tab}
            article={article}
            provider={tab}
            disabled={busy}
            busyUrl={importStock.isPending ? importStock.variables?.download_url : null}
            onPick={(p) => importStock.mutate(p)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
